import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';

const FormContext = createContext();

// Form action types
const FORM_ACTIONS = {
  // Release form actions
  UPDATE_RELEASE_FORM: 'UPDATE_RELEASE_FORM',
  RESET_RELEASE_FORM: 'RESET_RELEASE_FORM',
  SET_RELEASE_FORM: 'SET_RELEASE_FORM',

  // Post form actions
  UPDATE_POST_FORM: 'UPDATE_POST_FORM',
  RESET_POST_FORM: 'RESET_POST_FORM',
  SET_POST_FORM: 'SET_POST_FORM',

  // Image management
  SET_MASTER_IMAGES: 'SET_MASTER_IMAGES',
  SET_RELEASE_IMAGES: 'SET_RELEASE_IMAGES',
  ADD_MASTER_IMAGE: 'ADD_MASTER_IMAGE',
  ADD_RELEASE_IMAGE: 'ADD_RELEASE_IMAGE',
  REMOVE_MASTER_IMAGE: 'REMOVE_MASTER_IMAGE',
  REMOVE_RELEASE_IMAGE: 'REMOVE_RELEASE_IMAGE',
  CLEAR_IMAGES: 'CLEAR_IMAGES',

  // Item selection
  SET_SELECTED_MASTER_ITEM: 'SET_SELECTED_MASTER_ITEM',
  SET_EDITING_MASTER_ITEM: 'SET_EDITING_MASTER_ITEM',
  SET_ADD_TO_COLLECTION: 'SET_ADD_TO_COLLECTION',
  SET_ADD_TO_WANTLIST: 'SET_ADD_TO_WANTLIST',

  // Search and scan data
  SET_BULK_IMPORT_TITLE: 'SET_BULK_IMPORT_TITLE',
  SET_IS_DATA_FROM_SEARCH: 'SET_IS_DATA_FROM_SEARCH',
  SET_SCANNED_MOVIE_DATA: 'SET_SCANNED_MOVIE_DATA',

  // Forum category
  SET_FORUM_CATEGORY: 'SET_FORUM_CATEGORY',

  // Reset all forms
  RESET_ALL_FORMS: 'RESET_ALL_FORMS',
};

// Initial form states
const initialReleaseForm = {
  release_title: '',
  studio: '',
  release_year: '',
  case_type: 'Slipcase',
  format: 'VHS',
  release_notes: '',
  release_country: 'United States',
  screener: false,
  distributor_code: '',
};

const initialPostForm = {
  title: '',
  content: '',
  category: 'general',
};

const initialFormState = {
  // Release form
  releaseForm: { ...initialReleaseForm },

  // Post form
  postForm: { ...initialPostForm },

  // Images
  selectedMasterImages: [],
  selectedReleaseImages: [],

  // Item selection
  selectedMasterItem: null,
  editingMasterItem: null,
  addToMyCollection: true,
  addToMyWantlist: false,

  // Search and scan
  bulkImportMovieTitle: '',
  isDataFromSearch: false,
  scannedMovieData: null,

  // Forum
  selectedForumCategory: 'general',
};

// Form reducer
const formReducer = (state, action) => {
  switch (action.type) {
    // Release form actions
    case FORM_ACTIONS.UPDATE_RELEASE_FORM:
      return {
        ...state,
        releaseForm: {
          ...state.releaseForm,
          ...action.payload,
        },
      };

    case FORM_ACTIONS.RESET_RELEASE_FORM:
      return {
        ...state,
        releaseForm: { ...initialReleaseForm },
      };

    case FORM_ACTIONS.SET_RELEASE_FORM:
      return {
        ...state,
        releaseForm: { ...action.payload },
      };

    // Post form actions
    case FORM_ACTIONS.UPDATE_POST_FORM:
      return {
        ...state,
        postForm: {
          ...state.postForm,
          ...action.payload,
        },
      };

    case FORM_ACTIONS.RESET_POST_FORM:
      return {
        ...state,
        postForm: { ...initialPostForm },
      };

    case FORM_ACTIONS.SET_POST_FORM:
      return {
        ...state,
        postForm: { ...action.payload },
      };

    // Image actions
    case FORM_ACTIONS.SET_MASTER_IMAGES:
      return {
        ...state,
        selectedMasterImages: action.payload,
      };

    case FORM_ACTIONS.SET_RELEASE_IMAGES:
      return {
        ...state,
        selectedReleaseImages: action.payload,
      };

    case FORM_ACTIONS.ADD_MASTER_IMAGE:
      return {
        ...state,
        selectedMasterImages: [...state.selectedMasterImages, action.payload],
      };

    case FORM_ACTIONS.ADD_RELEASE_IMAGE:
      return {
        ...state,
        selectedReleaseImages: [...state.selectedReleaseImages, action.payload],
      };

    case FORM_ACTIONS.REMOVE_MASTER_IMAGE:
      return {
        ...state,
        selectedMasterImages: state.selectedMasterImages.filter(
          (_, index) => index !== action.payload
        ),
      };

    case FORM_ACTIONS.REMOVE_RELEASE_IMAGE:
      return {
        ...state,
        selectedReleaseImages: state.selectedReleaseImages.filter(
          (_, index) => index !== action.payload
        ),
      };

    case FORM_ACTIONS.CLEAR_IMAGES:
      return {
        ...state,
        selectedMasterImages: [],
        selectedReleaseImages: [],
      };

    // Item selection actions
    case FORM_ACTIONS.SET_SELECTED_MASTER_ITEM:
      return {
        ...state,
        selectedMasterItem: action.payload,
      };

    case FORM_ACTIONS.SET_EDITING_MASTER_ITEM:
      return {
        ...state,
        editingMasterItem: action.payload,
      };

    case FORM_ACTIONS.SET_ADD_TO_COLLECTION:
      return {
        ...state,
        addToMyCollection: action.payload,
      };

    case FORM_ACTIONS.SET_ADD_TO_WANTLIST:
      return {
        ...state,
        addToMyWantlist: action.payload,
      };

    // Search and scan actions
    case FORM_ACTIONS.SET_BULK_IMPORT_TITLE:
      return {
        ...state,
        bulkImportMovieTitle: action.payload,
      };

    case FORM_ACTIONS.SET_IS_DATA_FROM_SEARCH:
      return {
        ...state,
        isDataFromSearch: action.payload,
      };

    case FORM_ACTIONS.SET_SCANNED_MOVIE_DATA:
      return {
        ...state,
        scannedMovieData: action.payload,
      };

    // Forum actions
    case FORM_ACTIONS.SET_FORUM_CATEGORY:
      return {
        ...state,
        selectedForumCategory: action.payload,
      };

    // Reset all
    case FORM_ACTIONS.RESET_ALL_FORMS:
      return { ...initialFormState };

    default:
      return state;
  }
};

export const FormProvider = React.memo(({ children }) => {
  const [formState, dispatch] = useReducer(formReducer, initialFormState);

  // Release form actions
  const updateReleaseForm = useCallback(updates => {
    dispatch({ type: FORM_ACTIONS.UPDATE_RELEASE_FORM, payload: updates });
  }, []);

  const resetReleaseForm = useCallback(() => {
    dispatch({ type: FORM_ACTIONS.RESET_RELEASE_FORM });
  }, []);

  const setReleaseForm = useCallback(formData => {
    dispatch({ type: FORM_ACTIONS.SET_RELEASE_FORM, payload: formData });
  }, []);

  // Post form actions
  const updatePostForm = useCallback(updates => {
    dispatch({ type: FORM_ACTIONS.UPDATE_POST_FORM, payload: updates });
  }, []);

  const resetPostForm = useCallback(() => {
    dispatch({ type: FORM_ACTIONS.RESET_POST_FORM });
  }, []);

  const setPostTitle = useCallback(title => {
    dispatch({ type: FORM_ACTIONS.UPDATE_POST_FORM, payload: { title } });
  }, []);

  const setPostContent = useCallback(content => {
    dispatch({ type: FORM_ACTIONS.UPDATE_POST_FORM, payload: { content } });
  }, []);

  // Image management actions
  const setSelectedMasterImages = useCallback(images => {
    dispatch({ type: FORM_ACTIONS.SET_MASTER_IMAGES, payload: images });
  }, []);

  const setSelectedReleaseImages = useCallback(images => {
    dispatch({ type: FORM_ACTIONS.SET_RELEASE_IMAGES, payload: images });
  }, []);

  const addMasterImage = useCallback(image => {
    dispatch({ type: FORM_ACTIONS.ADD_MASTER_IMAGE, payload: image });
  }, []);

  const addReleaseImage = useCallback(image => {
    dispatch({ type: FORM_ACTIONS.ADD_RELEASE_IMAGE, payload: image });
  }, []);

  const removeMasterImage = useCallback(index => {
    dispatch({ type: FORM_ACTIONS.REMOVE_MASTER_IMAGE, payload: index });
  }, []);

  const removeReleaseImage = useCallback(index => {
    dispatch({ type: FORM_ACTIONS.REMOVE_RELEASE_IMAGE, payload: index });
  }, []);

  const clearAllImages = useCallback(() => {
    dispatch({ type: FORM_ACTIONS.CLEAR_IMAGES });
  }, []);

  // Item selection actions
  const setSelectedMasterItem = useCallback(item => {
    dispatch({ type: FORM_ACTIONS.SET_SELECTED_MASTER_ITEM, payload: item });
  }, []);

  const setEditingMasterItem = useCallback(item => {
    dispatch({ type: FORM_ACTIONS.SET_EDITING_MASTER_ITEM, payload: item });
  }, []);

  const setAddToMyCollection = useCallback(value => {
    dispatch({ type: FORM_ACTIONS.SET_ADD_TO_COLLECTION, payload: value });
  }, []);

  const setAddToMyWantlist = useCallback(value => {
    dispatch({ type: FORM_ACTIONS.SET_ADD_TO_WANTLIST, payload: value });
  }, []);

  // Search and scan actions
  const setBulkImportMovieTitle = useCallback(title => {
    dispatch({ type: FORM_ACTIONS.SET_BULK_IMPORT_TITLE, payload: title });
  }, []);

  const setIsDataFromSearch = useCallback(value => {
    dispatch({ type: FORM_ACTIONS.SET_IS_DATA_FROM_SEARCH, payload: value });
  }, []);

  const setScannedMovieData = useCallback(data => {
    dispatch({ type: FORM_ACTIONS.SET_SCANNED_MOVIE_DATA, payload: data });
  }, []);

  // Forum actions
  const setSelectedForumCategory = useCallback(category => {
    dispatch({ type: FORM_ACTIONS.SET_FORUM_CATEGORY, payload: category });
  }, []);

  // Utility actions
  const resetAllForms = useCallback(() => {
    dispatch({ type: FORM_ACTIONS.RESET_ALL_FORMS });
  }, []);

  const resetFormsWithImages = useCallback(() => {
    resetReleaseForm();
    clearAllImages();
    setIsDataFromSearch(false);
  }, [resetReleaseForm, clearAllImages, setIsDataFromSearch]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      // State
      ...formState,

      // Release form actions
      updateReleaseForm,
      resetReleaseForm,
      setReleaseForm,

      // Post form actions
      updatePostForm,
      resetPostForm,
      setPostTitle,
      setPostContent,

      // Image actions
      setSelectedMasterImages,
      setSelectedReleaseImages,
      addMasterImage,
      addReleaseImage,
      removeMasterImage,
      removeReleaseImage,
      clearAllImages,

      // Item selection actions
      setSelectedMasterItem,
      setEditingMasterItem,
      setAddToMyCollection,
      setAddToMyWantlist,

      // Search and scan actions
      setBulkImportMovieTitle,
      setIsDataFromSearch,
      setScannedMovieData,

      // Forum actions
      setSelectedForumCategory,

      // Utility actions
      resetAllForms,
      resetFormsWithImages,
    }),
    [
      formState,
      updateReleaseForm,
      resetReleaseForm,
      setReleaseForm,
      updatePostForm,
      resetPostForm,
      setPostTitle,
      setPostContent,
      setSelectedMasterImages,
      setSelectedReleaseImages,
      addMasterImage,
      addReleaseImage,
      removeMasterImage,
      removeReleaseImage,
      clearAllImages,
      setSelectedMasterItem,
      setEditingMasterItem,
      setAddToMyCollection,
      setAddToMyWantlist,
      setBulkImportMovieTitle,
      setIsDataFromSearch,
      setScannedMovieData,
      setSelectedForumCategory,
      resetAllForms,
      resetFormsWithImages,
    ]
  );

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
});

export const useForm = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useForm must be used within a FormProvider');
  }
  return context;
};

// Export form action types and initial states for convenience
export { FORM_ACTIONS, initialReleaseForm, initialPostForm };
